import React, { useState } from 'react'
import { authAPI } from '../api/client'

/**
 * Change the signed-in user's own password (POST /api/auth/change-password).
 *
 * Shared by the tenant Account page and the admin Settings page. The server
 * re-issues the session cookie, so the user stays signed in here while every
 * other session is signed out.
 */
export const ChangePasswordForm: React.FC = () => {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    if (next !== confirm) {
      setError('New passwords do not match')
      return
    }
    if (next.length < 8) {
      setError('New password must be at least 8 characters')
      return
    }
    setSubmitting(true)
    try {
      await authAPI.changePassword(current, next)
      setCurrent('')
      setNext('')
      setConfirm('')
      setSuccess('Password changed. Other devices have been signed out.')
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ChangePasswordForm] change failed', err)
      setError(err instanceof Error ? err.message : 'Failed to change password')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div data-testid="change-password" className="card">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Change password</h2>

      {error && (
        <div
          data-testid="change-password-error"
          role="alert"
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4"
        >
          {error}
        </div>
      )}
      {success && (
        <div
          data-testid="change-password-success"
          role="status"
          className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded mb-4"
        >
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        <div>
          <label htmlFor="current-password" className="label">Current password</label>
          <input
            id="current-password"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="input w-full"
          />
        </div>
        <div>
          <label htmlFor="new-password" className="label">New password</label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            required
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="input w-full"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">At least 8 characters.</p>
        </div>
        <div>
          <label htmlFor="confirm-new-password" className="label">Confirm new password</label>
          <input
            id="confirm-new-password"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="input w-full"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Changing…' : 'Change password'}
        </button>
      </form>
    </div>
  )
}
